import { themeAtom } from "../../../shared/atoms";
import { formatUndefinedCellToNA } from "../../../shared/excel-grid/utils/excel-grid.utils";
import type { OnboardingCertificateCandidatesTableProps } from "../../models/onboarding-certificate-candidates-table.models";
import { getVesselCandidateRows } from "../../utils/admin-debug.utils";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

const OnboardingCertificateCandidatesTable = ({
  result,
  selectedImoNumber,
  onSelectCandidate,
}: OnboardingCertificateCandidatesTableProps) => {
  const theme = useAtomValue(themeAtom);
  const rows = getVesselCandidateRows(result);

  return (
    <section
      className={twMerge("mb-5 overflow-hidden p-4 shadow-sm", theme.home.tailwind.panelBackground)}
      data-test="admin-onboarding-certificate-candidates"
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className={twMerge("text-sm font-semibold", theme.home.tailwind.textDefault)}>
            Onboarding certificate request candidates
          </h3>
          <p className={twMerge("mt-1 text-xs", theme.uwhcAssistant.textOutput)}>
            Select one candidate to use its IMO number for Send test email.
          </p>
        </div>
        <span className="text-xs opacity-70">{rows.length.toLocaleString()} candidates</span>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-gray-300 px-4 py-6 text-center text-sm opacity-75">
          No eligible request candidates returned by this dry run.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className={twMerge("text-xs uppercase", theme.uwhcAssistant.text)}>
              <tr className="border-b border-gray-200">
                <th className="px-3 py-2 font-semibold">Vessel</th>
                <th className="px-3 py-2 font-semibold">IMO</th>
                <th className="px-3 py-2 font-semibold">Vessel code</th>
                <th className="px-3 py-2 font-semibold">Master email</th>
                <th className="px-3 py-2 font-semibold">Delivery date</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((candidate) => {
                const imoNumber = formatUndefinedCellToNA(candidate.IMO_NUMBER);
                const isSelected = selectedImoNumber === imoNumber;

                return (
                  <tr key={`${imoNumber}-${candidate.VESSELCODE ?? ""}`} className="border-b border-gray-100">
                    <td className="px-3 py-2">{formatUndefinedCellToNA(candidate.VESSEL_NAME)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{imoNumber}</td>
                    <td className="px-3 py-2">{formatUndefinedCellToNA(candidate.VESSELCODE)}</td>
                    <td className="px-3 py-2">{formatUndefinedCellToNA(candidate.EMAIL)}</td>
                    <td className="px-3 py-2">{formatUndefinedCellToNA(candidate.DELIVERYDATE)}</td>
                    <td className="px-3 py-2">{formatUndefinedCellToNA(candidate.STATUS)}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        aria-label={
                          isSelected ? `Selected candidate IMO ${imoNumber}` : `Select candidate IMO ${imoNumber}`
                        }
                        className={twMerge(
                          "inline-flex h-8 items-center justify-center border border-gray-300 px-3 text-xs font-semibold transition-colors hover:border-green",
                          isSelected ? "border-green bg-[#CCE4E6] text-[#007681]" : "",
                        )}
                        onClick={() => onSelectCandidate(imoNumber)}
                      >
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default OnboardingCertificateCandidatesTable;
